import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as d3 from "d3";
import axios from "axios";
import DeployStageIndicator from "../../components/deploy/DeployStageIndicator";
import ProjectInfrastructureMap from "../../components/deploy/ProjectInfrastructureMap";

const api = axios.create({ baseURL: "", withCredentials: true });

const STATUS_COLOR = {
  healthy:      "#22c55e",
  active:       "#22c55e",
  deployed:     "#22c55e",
  completed:    "#22c55e",
  degraded:     "#f59e0b",
  pending:      "#f59e0b",
  provisioning: "#f59e0b",
  recovering:   "#f59e0b",
  running:      "#f59e0b",
  critical:     "#ef4444",
  failed:       "#ef4444",
  cancelled:    "#6b7280",
  stopped:      "#6b7280",
  destroyed:    "#6b7280",
};

const JOB_TYPE_LABEL = {
  deploy:    "Deploy",
  redeploy:  "Redeploy",
  restart:   "Restart",
  rollback:  "Rollback",
  destroy:   "Destroy",
  backup:    "Backup",
  scale:     "Scale",
  diagnose:  "Diagnose",
};

// ── D3 Deployment Timeline ─────────────────────────────────────────────────

function DeploymentTimeline({ jobs }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!svgRef.current || !jobs.length) return;

    const containerW = containerRef.current?.clientWidth || 800;
    const margin = { top: 28, right: 20, bottom: 34, left: 20 };
    const H = 110;
    const innerW = containerW - margin.left - margin.right;
    const innerH = H - margin.top - margin.bottom;
    const cy = innerH / 2;

    const el = svgRef.current;
    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el)
      .attr("width", containerW)
      .attr("height", H)
      .attr("viewBox", `0 0 ${containerW} ${H}`);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const sorted = [...jobs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const times = sorted.map(j => new Date(j.createdAt));
    const minTime = d3.min(times);
    const maxTime = new Date(Math.max(Date.now(), d3.max(times).getTime() + 3_600_000));

    const x = d3.scaleTime().domain([minTime, maxTime]).range([0, innerW]).nice();

    // Baseline
    g.append("line")
      .attr("x1", 0).attr("y1", cy).attr("x2", innerW).attr("y2", cy)
      .attr("stroke", "#374151").attr("stroke-width", 2).attr("stroke-linecap", "round");

    // X axis
    g.append("g")
      .attr("transform", `translate(0,${innerH + 4})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(sorted.length + 1, 8))
          .tickFormat(d3.timeFormat(sorted.length <= 1 ? "%b %d, %H:%M" : "%b %d"))
      )
      .call(a => a.select(".domain").remove())
      .call(a => a.selectAll("line").attr("stroke", "#374151"))
      .call(a => a.selectAll("text").attr("fill", "#6b7280").attr("font-size", "10px"));

    // Job groups
    const dots = g.selectAll(".jdot")
      .data(sorted)
      .enter()
      .append("g")
      .attr("class", "jdot")
      .attr("transform", d => `translate(${x(new Date(d.createdAt))},${cy})`)
      .style("cursor", "pointer");

    // Glow ring
    dots.append("circle")
      .attr("r", 14)
      .attr("fill", d => `${STATUS_COLOR[d.status] || "#6b7280"}22`);

    // Main dot
    dots.append("circle")
      .attr("r", 8)
      .attr("fill", d => STATUS_COLOR[d.status] || "#6b7280")
      .attr("stroke", "#000")
      .attr("stroke-width", 1.5);

    // Label above
    dots.append("text")
      .attr("y", -18)
      .attr("text-anchor", "middle")
      .attr("fill", "#9ca3af")
      .attr("font-size", "9px")
      .attr("font-family", "monospace")
      .text(d => (JOB_TYPE_LABEL[d.jobType] || d.jobType).slice(0, 8));

    // Hover
    dots
      .on("mouseover", function (event, d) {
        d3.select(this).selectAll("circle").filter((_, i) => i === 1).attr("r", 11);
        const dotX = x(new Date(d.createdAt)) + margin.left;
        const dotY = cy + margin.top;
        setTooltip({ d, x: dotX, y: dotY });
      })
      .on("mouseout", function () {
        d3.select(this).selectAll("circle").filter((_, i) => i === 1).attr("r", 8);
        setTooltip(null);
      });
  }, [jobs]);

  return (
    <div ref={containerRef} className="relative w-full">
      <svg ref={svgRef} className="w-full overflow-visible" />
      {tooltip && (
        <div
          className="absolute z-20 pointer-events-none bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl"
          style={{ left: Math.max(0, Math.min(tooltip.x - 80, (containerRef.current?.clientWidth || 800) - 200)), top: tooltip.y + 16 }}
        >
          <p className="text-silver-light font-medium">{JOB_TYPE_LABEL[tooltip.d.jobType] || tooltip.d.jobType}</p>
          <p className="mt-0.5" style={{ color: STATUS_COLOR[tooltip.d.status] || "#6b7280" }}>{tooltip.d.status}</p>
          <p className="text-silver-dark mt-0.5">{new Date(tooltip.d.createdAt).toLocaleString()}</p>
          {tooltip.d.finishedAt && (
            <p className="text-silver-dark">
              {Math.round((new Date(tooltip.d.finishedAt) - new Date(tooltip.d.createdAt)) / 1000)}s
            </p>
          )}
          {tooltip.d.error && (
            <p className="text-red-400 mt-1 max-w-[200px] truncate">{tooltip.d.error.message || tooltip.d.error.code}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (!status) return null;
  const color = STATUS_COLOR[status] || "#6b7280";
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs border rounded-full px-2.5 py-0.5 font-medium capitalize"
      style={{ borderColor: `${color}55`, color, background: `${color}15` }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      {status}
    </span>
  );
}

function ResourceCard({ icon, label, statusColor, children }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-base leading-none">{icon}</span>
        <span className="text-xs uppercase tracking-wider text-silver-dark font-medium">{label}</span>
        {statusColor && (
          <span className="ml-auto w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
        )}
      </div>
      <div className="text-xs text-silver-medium space-y-1.5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === "" || value === false) return null;
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-silver-dark flex-shrink-0">{label}</span>
      <span className="text-silver-light text-right font-mono truncate max-w-[160px]" title={String(value)}>{String(value)}</span>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-off-black border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-silver-dark mb-1">{label}</p>
      <p className="text-xl font-mono font-medium" style={{ color: color || "#e2e8f0" }}>{value}</p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function DeployProjectDetailPage() {
  const { slug } = useParams();

  const [project, setProject] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [infraNodes, setInfraNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState({});
  const [jobDetailLoading, setJobDetailLoading] = useState(false);
  const [listing, setListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(true);
  const [listingToggling, setListingToggling] = useState(false);
  const [boostConfirm, setBoostConfirm] = useState(null); // "spotlight" | "pro" | null
  const [boostLoading, setBoostLoading] = useState(false);
  const [boostError, setBoostError] = useState("");
  // Boost tiers come from the canonical /api/marketplace/tiers so prices never
  // drift from the backend charge. Fallback mirrors marketplaceBoostTiers.js.
  const [tiers, setTiers] = useState([
    { id: "spotlight", label: "Spotlight", priceLabel: "€5", days: 7 },
    { id: "pro", label: "Pro", priceLabel: "€12", days: 30 },
  ]);
  const tierById = (id) => tiers.find((t) => t.id === id) || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [projRes, infraRes] = await Promise.all([
        api.get(`/api/deploy/projects/${encodeURIComponent(slug)}`),
        api.get("/api/deploy/infrastructure/graph").catch(() => ({ data: null })),
      ]);
      setProject(projRes.data.project);
      setJobs(projRes.data.recentDeployments || []);

      if (infraRes.data?.graph) {
        const { nodes = [], edges = [] } = infraRes.data.graph;
        const projNode = nodes.find(
          n => n.type === "project" && (n.id.includes(slug) || String(n.label).toLowerCase() === slug)
        );
        if (projNode) {
          const connected = new Set(
            edges
              .filter(e => e.source === projNode.id || e.target === projNode.id)
              .flatMap(e => [e.source, e.target])
          );
          connected.delete(projNode.id);
          setInfraNodes(nodes.filter(n => connected.has(n.id)));
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Could not load project");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setListingLoading(true);
    api.post(`/api/marketplace/manage/listings/from-project/${encodeURIComponent(slug)}`)
      .then(r => setListing(r.data.listing))
      .catch(() => setListing(null))
      .finally(() => setListingLoading(false));
  }, [slug]);

  useEffect(() => {
    api.get("/api/marketplace/tiers")
      .then(r => { if (Array.isArray(r.data?.tiers) && r.data.tiers.length) setTiers(r.data.tiers); })
      .catch(() => { /* keep fallback tiers */ });
  }, []);

  const handleBoost = async (tier) => {
    if (!listing || boostLoading) return;
    setBoostLoading(true);
    setBoostError("");
    try {
      const { data } = await api.post(`/api/marketplace/manage/listings/${listing._id}/boost`, { tier });
      setListing(data.listing);
      setBoostConfirm(null);
    } catch (e) {
      const msg = e.response?.data?.error || "Boost failed. Try again.";
      const portalRequired = e.response?.data?.billingPortalRequired;
      setBoostError(portalRequired ? `${msg} → Update in billing settings.` : msg);
    } finally {
      setBoostLoading(false);
    }
  };

  const toggleListingVisibility = async () => {
    if (!listing || listingToggling) return;
    setListingToggling(true);
    try {
      const { data } = await api.patch(`/api/marketplace/manage/listings/${listing._id}/visibility`);
      setListing(data.listing);
    } catch { /* silent */ } finally {
      setListingToggling(false);
    }
  };

  const toggleJob = async (jobId) => {
    if (expandedJob === jobId) { setExpandedJob(null); return; }
    setExpandedJob(jobId);
    if (jobDetail[jobId]) return;
    setJobDetailLoading(true);
    try {
      const { data } = await api.get(`/api/deploy/projects/${encodeURIComponent(slug)}/deployments/${jobId}`);
      setJobDetail(prev => ({ ...prev, [jobId]: data.deployment }));
    } catch {
      setJobDetail(prev => ({ ...prev, [jobId]: { _err: "Could not load steps." } }));
    } finally {
      setJobDetailLoading(false);
    }
  };

  // Poll the expanded job every 3 s while it is queued or running so stages update live.
  const pollRef = useRef(null);
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!expandedJob) return;
    const activeJob = jobs.find(j => j.id === expandedJob);
    if (!activeJob || !["queued", "running"].includes(activeJob.status)) return;

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/api/deploy/projects/${encodeURIComponent(slug)}/deployments/${expandedJob}`);
        setJobDetail(prev => ({ ...prev, [expandedJob]: data.deployment }));
      } catch { /* silent — don't interrupt UI on poll failure */ }
    }, 3000);

    return () => clearInterval(pollRef.current);
    // `jobs` is read once, at subscribe time, purely to decide whether this job is still
    // running. Adding it to the deps would tear down and re-create the interval on every
    // list update — including the updates this very poll causes — so the omission is the
    // correct behaviour, not an oversight. Silenced explicitly because under CI=1 this
    // warning is an ERROR, and it was the single thing blocking every CRA build in CI.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedJob, slug]);

  if (loading) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-400 text-sm">{error || "Project not found"}</p>
          <Link to="/limelite/deploy/dashboard" className="text-lime-500 hover:text-lime-400 text-sm underline">
            ← Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const rdsNode = infraNodes.find(n => n.type === "rds");
  const successCount = jobs.filter(j => j.status === "completed").length;
  const failedCount  = jobs.filter(j => j.status === "failed").length;
  const lastDeploy   = jobs.find(j => ["deploy", "redeploy"].includes(j.jobType));

  const hasCloudFront   = Boolean(project.cloudFrontDistributionId || project.cloudFrontDomainName);
  const hasCustomDomain = Boolean(project.customDomainFqdn);
  const hasSes          = Boolean(project.sesDomain);
  const hasAlb          = Boolean(project.albArn);
  const hasAsg          = Boolean(project.autoScalingGroupName);
  const hasCron         = Array.isArray(project.cronSchedules) && project.cronSchedules.length > 0;
  const hasPreview      = Array.isArray(project.previewEnvironments) && project.previewEnvironments.length > 0;

  const liveUrl = project.cloudFrontPublicUrl || project.publicUrl || "";

  return (
    <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 sm:px-6 py-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Back */}
        <Link
          to="/limelite/deploy/dashboard"
          className="inline-flex items-center gap-1.5 text-silver-dark hover:text-silver-light text-sm transition-colors group"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-0.5 transition-transform" aria-hidden="true">
            <path d="m15 18-6-6 6-6" />
          </svg>
          All projects
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-mono font-semibold">{project.slug}</h1>
              {project.displayName && project.displayName !== project.slug && (
                <span className="text-silver-dark text-sm">— {project.displayName}</span>
              )}
              <StatusBadge status={project.status} />
              {["healthy", "deployed", "degraded", "recovering", "critical"].includes(project.status) && project.healthFsmState && project.healthFsmState !== project.status && (
                <StatusBadge status={project.healthFsmState} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-silver-dark">
              {project.ec2InstanceType && <span>⚙ {project.ec2InstanceType}</span>}
              {project.awsRegion       && <span>📍 {project.awsRegion}</span>}
              {project.runtime         && <span>◈ {project.runtime}</span>}
              {project.branch          && <span>⎇ {project.branch}</span>}
              {project.gitCommitSha    && <span className="font-mono">{project.gitCommitSha.slice(0, 8)}</span>}
              {project.createdAt       && <span>since {new Date(project.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
            </div>
          </div>
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-lime-500 hover:text-lime-400 border border-lime-900/60 hover:border-lime-700 rounded-lg px-3 py-2 transition-colors flex-shrink-0"
            >
              Open app
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total jobs"    value={jobs.length} />
          <StatCard label="Successful"    value={successCount} color={successCount > 0 ? "#22c55e" : undefined} />
          <StatCard label="Failed"        value={failedCount}  color={failedCount  > 0 ? "#ef4444" : undefined} />
          <StatCard
            label="Last deploy"
            value={lastDeploy ? new Date(lastDeploy.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
          />
        </div>

        <ProjectInfrastructureMap
          project={project}
          infraNodes={infraNodes}
          liveUrl={liveUrl}
        />

        {/* D3 Timeline */}
        <section className="bg-off-black border border-gray-800 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-medium">Deployment timeline</h2>
            <span className="text-silver-dark text-xs">{jobs.length} job{jobs.length !== 1 ? "s" : ""}</span>
          </div>
          <p className="text-silver-dark text-xs mb-5">Hover a dot to inspect · color = status</p>

          {jobs.length === 0 ? (
            <p className="text-silver-dark text-sm">No deployments recorded yet.</p>
          ) : (
            <>
              <DeploymentTimeline jobs={jobs} />
              <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t border-gray-800">
                {Object.entries(STATUS_COLOR)
                  .filter(([s]) => jobs.some(j => j.status === s))
                  .filter(([s], _, arr) => arr.findIndex(([ss]) => STATUS_COLOR[ss] === STATUS_COLOR[s]) === arr.findIndex(([ss]) => ss === s))
                  .map(([s, c]) => (
                    <span key={s} className="flex items-center gap-1.5 text-xs text-silver-dark">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c }} />
                      {s}
                    </span>
                  ))}
              </div>
            </>
          )}
        </section>

        {/* Infrastructure */}
        <section className="bg-off-black border border-gray-800 rounded-xl p-4 sm:p-6">
          <h2 className="text-base font-medium mb-4">Infrastructure</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

            <ResourceCard icon="⚙" label="EC2 Compute" statusColor={STATUS_COLOR[project.status]}>
              <InfoRow label="Instance" value={project.ec2InstanceId ? project.ec2InstanceId.slice(0, 21) : null} />
              <InfoRow label="Type"     value={project.ec2InstanceType} />
              <InfoRow label="Region"   value={project.awsRegion} />
              <InfoRow label="SSM"      value={project.ssmAgentPingStatus || null} />
              {project.observabilityLastCpuMaxPct  != null && <InfoRow label="CPU last"  value={`${project.observabilityLastCpuMaxPct}%`} />}
              {project.observabilityLastDiskUsedPct != null && <InfoRow label="Disk last" value={`${project.observabilityLastDiskUsedPct}%`} />}
              {project.appPort && <InfoRow label="Port" value={project.appPort} />}
            </ResourceCard>

            {hasCloudFront && (
              <ResourceCard icon="🌐" label="CloudFront CDN" statusColor={STATUS_COLOR[project.cloudFrontStatus]}>
                <InfoRow label="Status"       value={project.cloudFrontStatus} />
                <InfoRow label="Domain"       value={project.cloudFrontDomainName} />
                <InfoRow label="Distribution" value={project.cloudFrontDistributionId ? project.cloudFrontDistributionId.slice(0, 16) + "…" : null} />
              </ResourceCard>
            )}

            {hasCustomDomain && (
              <ResourceCard icon="🏷" label="Custom Domain" statusColor={project.customDomainStatus === "active" ? "#22c55e" : "#f59e0b"}>
                <InfoRow label="Domain"  value={project.customDomainFqdn} />
                <InfoRow label="Status"  value={project.customDomainStatus} />
                <InfoRow label="Route53" value={project.route53RecordFqdn} />
                {project.sslCertNotAfter && (
                  <InfoRow label="SSL exp." value={new Date(project.sslCertNotAfter).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} />
                )}
              </ResourceCard>
            )}

            {hasSes && (
              <ResourceCard icon="✉" label="Email (SES)" statusColor={project.sesVerificationStatus === "Success" ? "#22c55e" : "#f59e0b"}>
                <InfoRow label="Domain"       value={project.sesDomain} />
                <InfoRow label="Verification" value={project.sesVerificationStatus} />
                <InfoRow label="DKIM"         value={project.sesDkimStatus} />
              </ResourceCard>
            )}

            {hasAlb && (
              <ResourceCard icon="⚖" label="Load Balancer">
                <InfoRow label="DNS" value={project.albDnsName} />
                <InfoRow label="ARN" value={project.albArn ? "…" + project.albArn.slice(-16) : null} />
              </ResourceCard>
            )}

            {hasAsg && (
              <ResourceCard icon="↕" label="Auto Scaling">
                <InfoRow label="Group" value={project.autoScalingGroupName} />
              </ResourceCard>
            )}

            {hasCron && (
              <ResourceCard icon="⏱" label="Scheduled Jobs">
                {project.cronSchedules.slice(0, 4).map((c, i) => (
                  <InfoRow key={i} label={c.name || `Job ${i + 1}`} value={c.schedule || c.expression} />
                ))}
                {project.cronSchedules.length > 4 && (
                  <p className="text-silver-dark">+{project.cronSchedules.length - 4} more</p>
                )}
              </ResourceCard>
            )}

            {hasPreview && (
              <ResourceCard icon="👁" label="Preview Envs">
                {project.previewEnvironments.slice(0, 3).map((p, i) => (
                  <InfoRow key={i} label={p.envName || `Env ${i + 1}`} value={p.status || p.publicUrl} />
                ))}
                {project.previewEnvironments.length > 3 && (
                  <p className="text-silver-dark">+{project.previewEnvironments.length - 3} more</p>
                )}
              </ResourceCard>
            )}

            {/* Database — from infra graph or placeholder */}
            {rdsNode ? (
              <ResourceCard icon="🗄" label="Database (RDS)" statusColor={STATUS_COLOR[rdsNode.status]}>
                <InfoRow label="Status"  value={rdsNode.status} />
                <InfoRow label="Instance" value={rdsNode.label} />
                {rdsNode.sublabel && <InfoRow label="Engine" value={rdsNode.sublabel} />}
                {rdsNode.awsId && <InfoRow label="ID" value={rdsNode.awsId.slice(0, 22)} />}
                {rdsNode.monthlyEstimateEur != null && (
                  <InfoRow label="Est. / mo" value={`€${Number(rdsNode.monthlyEstimateEur).toFixed(2)}`} />
                )}
              </ResourceCard>
            ) : project.atlasProjectId ? (
              <ResourceCard icon="🗄" label="Database (MongoDB Atlas)">
                <p className="text-silver-light">Managed database provisioned for this store.</p>
                {project.atlasClusterName && <InfoRow label="Cluster" value={project.atlasClusterName} />}
                <p className="text-silver-dark mt-1">Application readiness is checked when deployment finishes.</p>
              </ResourceCard>
            ) : (
              <ResourceCard icon="🗄" label="Database">
                <p className="text-silver-dark italic">No managed database attached.</p>
                <p className="text-silver-dark mt-1">
                  Use the MCP <code className="text-silver-light not-italic">create_database</code> tool to provision one.
                </p>
              </ResourceCard>
            )}

          </div>
        </section>

        {/* Marketplace */}
        <section className="bg-off-black border border-gray-800 rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium">Marketplace listing</h2>
            {listing && (
              <a
                href={`/limelite/marketplace/${listing.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-silver-dark hover:text-silver-light transition-colors"
              >
                View listing →
              </a>
            )}
          </div>

          {listingLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 border border-lime-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <p className="text-silver-dark text-xs">Loading…</p>
            </div>
          ) : listing ? (
            <div className="space-y-5">

              {/* Status row */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm text-silver-medium font-medium truncate">{listing.name}</p>
                  <p className="text-xs text-silver-dark mt-0.5">{listing.tagline}</p>
                </div>
                {(() => {
                  const now = new Date();
                  const va = listing.visibleAt ? new Date(listing.visibleAt) : null;
                  if (!listing.visible) return (
                    <span className="text-xs text-silver-dark border border-gray-700 rounded-full px-2.5 py-0.5 flex-shrink-0">Hidden</span>
                  );
                  if (va && va > now) {
                    const hrs = Math.ceil((va - now) / 3_600_000);
                    return <span className="text-xs text-amber-400 border border-amber-800/40 rounded-full px-2.5 py-0.5 flex-shrink-0">Live in ~{hrs}h</span>;
                  }
                  return <span className="text-xs text-lime-400 border border-lime-800/40 rounded-full px-2.5 py-0.5 flex-shrink-0">Live</span>;
                })()}
              </div>

              {/* Active boost badge */}
              {listing.featured && listing.featuredUntil && new Date(listing.featuredUntil) > new Date() && (
                <div className="flex items-center gap-2 bg-amber-900/20 border border-amber-800/30 rounded-lg px-3 py-2">
                  <span className="text-amber-300 text-xs font-medium">
                    {listing.featuredTier === "pro" ? "Pro Boost" : "Spotlight"} active
                  </span>
                  <span className="text-amber-500/60 text-xs">·</span>
                  <span className="text-amber-400/80 text-xs">
                    expires {new Date(listing.featuredUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </span>
                </div>
              )}

              {/* Visibility toggle */}
              <button
                type="button"
                onClick={toggleListingVisibility}
                disabled={listingToggling}
                className={`text-sm border rounded-lg px-4 py-2 transition-colors disabled:opacity-50 ${
                  listing.visible
                    ? "border-gray-700 text-silver-medium hover:border-gray-500"
                    : "border-lime-800/40 text-lime-400 hover:border-lime-600"
                }`}
              >
                {listingToggling ? "Saving…" : listing.visible ? "Hide from marketplace" : "Show in marketplace"}
              </button>

              {/* Boost section */}
              <div className="border-t border-gray-800 pt-4">
                <p className="text-xs text-silver-dark mb-3 uppercase tracking-wider">Boost listing</p>
                <p className="text-xs text-silver-dark mb-4">
                  Featured listings appear at the top of the marketplace in a randomised rotation — equal impressions for everyone. Charged to your saved payment method.
                </p>

                {boostConfirm ? (
                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 space-y-3">
                    <p className="text-sm text-silver-light">
                      Charge{" "}
                      <span className="text-lime-400 font-medium">
                        {tierById(boostConfirm)?.priceLabel || ""}
                      </span>{" "}
                      to your saved card for{" "}
                      <span className="text-lime-400 font-medium">
                        {tierById(boostConfirm)?.days} days
                      </span>{" "}
                      of featured placement?
                      {listing.featured && listing.featuredUntil && new Date(listing.featuredUntil) > new Date() && (
                        <span className="text-silver-dark text-xs block mt-1">Time will be added to your current boost.</span>
                      )}
                    </p>
                    {boostError && <p className="text-red-400 text-xs">{boostError}</p>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleBoost(boostConfirm)}
                        disabled={boostLoading}
                        className="text-sm bg-lime-600 hover:bg-lime-500 text-black font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {boostLoading ? "Charging…" : "Confirm charge"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setBoostConfirm(null); setBoostError(""); }}
                        disabled={boostLoading}
                        className="text-sm border border-gray-700 text-silver-dark px-4 py-2 rounded-lg hover:border-gray-500 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 flex-wrap">
                    {tiers.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => { setBoostConfirm(t.id); setBoostError(""); }}
                        className="flex-1 min-w-[140px] text-left border border-gray-700 hover:border-amber-700/60 rounded-xl p-3 transition-colors group"
                      >
                        <p className="text-sm font-medium text-silver-light group-hover:text-amber-300 transition-colors">{t.label}</p>
                        <p className="text-xs text-silver-dark mt-0.5">{t.days} days featured</p>
                        <p className="text-lg font-mono text-lime-400 mt-2">{t.priceLabel}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

            </div>
          ) : (
            <p className="text-silver-dark text-sm">Listing unavailable.</p>
          )}
        </section>

        {/* Deployment list */}
        <section className="bg-off-black border border-gray-800 rounded-xl p-4 sm:p-6">
          <h2 className="text-base font-medium mb-4">Recent deployments</h2>
          {jobs.length === 0 ? (
            <p className="text-silver-dark text-sm">No deployments yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {jobs.map(j => {
                const detail = jobDetail[j.id];
                const open   = expandedJob === j.id;
                const dur    = j.finishedAt
                  ? Math.round((new Date(j.finishedAt) - new Date(j.createdAt)) / 1000)
                  : null;

                return (
                  <li key={j.id}>
                    <button
                      type="button"
                      onClick={() => toggleJob(j.id)}
                      className={`w-full text-left flex items-center gap-3 border rounded-lg px-3 py-2.5 text-sm transition-colors ${
                        open ? "border-gray-600 rounded-b-none" : "border-gray-800 hover:border-gray-600"
                      }`}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: STATUS_COLOR[j.status] || "#6b7280" }}
                      />
                      <span className="text-silver-light font-medium w-20 flex-shrink-0">
                        {JOB_TYPE_LABEL[j.jobType] || j.jobType}
                      </span>
                      <span className="text-silver-dark text-xs">
                        {new Date(j.createdAt).toLocaleString()}
                      </span>
                      {dur !== null && (
                        <span className="text-silver-dark text-xs ml-auto flex-shrink-0">{dur}s</span>
                      )}
                      {j.error && (
                        <span className="text-red-400 text-xs truncate max-w-[180px]">
                          {j.error.message || j.error.code}
                        </span>
                      )}
                      <svg
                        xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        className={`ml-auto flex-shrink-0 text-silver-dark transition-transform ${open ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {open && (
                      <div className="border border-t-0 border-gray-600 rounded-b-lg px-4 py-4 bg-black/40">
                        {jobDetailLoading && !detail ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 border border-lime-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            <p className="text-silver-dark text-xs">Loading…</p>
                          </div>
                        ) : detail?._err ? (
                          <p className="text-red-400 text-xs">{detail._err}</p>
                        ) : (
                          <>
                            {/* Stage progress indicator — only shown for jobs that have stages */}
                            <DeployStageIndicator stages={detail?.stages} />

                            {/* Step log */}
                            {detail?.steps?.length > 0 ? (
                              <>
                                {detail?.stages?.length > 0 && (
                                  <p className="text-[10px] text-silver-dark uppercase tracking-widest mb-2 font-semibold">
                                    Log
                                  </p>
                                )}
                                <ol className="space-y-1 max-h-64 overflow-y-auto">
                                  {detail.steps.map((s, i) => (
                                    <li key={i} className="flex gap-3 text-xs font-mono">
                                      <span className="text-silver-dark flex-shrink-0 w-20">
                                        {s.at ? new Date(s.at).toLocaleTimeString() : ""}
                                      </span>
                                      <span className={`flex-shrink-0 w-28 truncate ${s.level === "error" ? "text-red-400" : s.level === "warn" ? "text-yellow-500" : "text-lime-700"}`}>
                                        {s.name}
                                      </span>
                                      <span className="text-silver-medium truncate">{s.message}</span>
                                    </li>
                                  ))}
                                </ol>
                              </>
                            ) : (
                              !detail?.stages?.length && (
                                <p className="text-silver-dark text-xs">No steps recorded.</p>
                              )
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

      </div>
    </div>
  );
}
